import type { AnnualTimetableCell, AnnualWeekdayKey, Grade } from '../types/domain';
import {
  getClassScheduleSettings,
  loadClassScheduleSettingsCache,
  resolveGradeProgramColors,
  type ClassScheduleSettings,
  type ProgramColorSettings,
} from '../services/sectionLayoutService';
import {
  buildAnnualCellKey,
  buildAnnualCellMap,
  buildAnnualClassEntries,
  isDedicatedColor,
} from './annualTimetable';
import { annualSlotHasTimetableProgramAssignment } from './annualProgressSlotSource';
import { loadAnnualProgressDataCache } from './annualProgressDataCache';
import { loadProgramProgressDataCache } from './programProgressDataCache';
import { dateToWeekdayKey, mondayIsoForDateInLocal } from './dashboardDateContext';
import { resolveAnnualProgressKeysForDate } from './dashboardTermResolve';
import { resolveMergedTimetableCellsByKey } from './timetableCellsByKeyResolve';
import { toISODate } from './date';
import { isWeekdayTimeKey, resolveClassScheduleRowsForWeekday } from './classScheduleWeekdayTimes';
import {
  ensureAnnualProgressMetaQueryData,
  ensureAnnualTimetableCellsQueryData,
  readAnnualProgressMetaCacheOrQuery,
  readAnnualTimetableCellsCacheOrQuery,
} from '../query/readSchoolQueryCache';
import type {
  ClassLessonAlarmOffset,
  ClassLessonAlarmSettings,
  ClassLessonAlarmSoundKind,
} from './classLessonAlarmSettings';
import { deviceCacheStorage } from './deviceCacheStorage';

export interface ClassLessonAlarmTarget {
  /** 교시 시작 시각 HH:mm */
  time: string;
  offset: ClassLessonAlarmOffset;
  soundKind: ClassLessonAlarmSoundKind;
  channel: 'regular' | 'dedicated';
  gradeId: string;
  gradeLabel: string;
  slotIndex: number;
  periodLabel: string;
  /** 연간시간표에 오늘 실제 수업(텍스트·색)이 편성됨 */
  hasLesson: boolean;
  /** 실제 울림 대상 (편성됨 + 해당 채널 사용) */
  willRing: boolean;
}

function normalizeHHMM(raw: string): string | null {
  const value = (raw ?? '').trim();
  const m = /^(\d{1,2})\s*:\s*([0-5]\d)$/.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || h < 0 || h > 23) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function scheduleHasClassStartTimes(schedule: ClassScheduleSettings): boolean {
  return Object.values(schedule.sectionRows ?? {}).some((rows) =>
    (rows ?? []).some((row) => row?.kind === 'class' && Boolean(normalizeHHMM(row.startTime ?? '')))
  );
}

/** 연간시간표 열이 있는 요일(월~토). 일요일만 제외 */
export function isClassLessonAlarmWeekdayKey(
  key: AnnualWeekdayKey | null | undefined
): key is AnnualWeekdayKey {
  return key === 'mon' || key === 'tue' || key === 'wed' || key === 'thu' || key === 'fri' || key === 'sat';
}

/** 대시보드·연간진도표와 동일: 오늘 날짜가 속한 학년도·학기 */
export function resolveAlarmAcademicYearTerm(
  workspaceId: string,
  now: Date = new Date(),
  programSidebarAcademicYear: number | null = null
): { academicYear: number; termNumber: 1 | 2 } {
  return resolveAnnualProgressKeysForDate(workspaceId, toISODate(now), programSidebarAcademicYear);
}

function overlayCellsIntoMap(
  target: Record<string, AnnualTimetableCell>,
  rows: AnnualTimetableCell[]
): void {
  if (!rows.length) return;
  Object.assign(target, buildAnnualCellMap(rows));
}

function rowsCoverDay(
  rows: AnnualTimetableCell[],
  mondayIso: string,
  weekdayKey: AnnualWeekdayKey
): boolean {
  return rows.some(
    (row) =>
      (row.monday_iso ?? '').trim() === mondayIso &&
      (row.weekday_key ?? '').trim() === weekdayKey
  );
}

/** 대시보드와 동일: 로컬 Date 기준 그 주의 월요일·요일 키 */
export function resolveAlarmDayKeys(now: Date): {
  dateIso: string;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey | null;
} {
  return {
    dateIso: toISODate(now),
    mondayIso: mondayIsoForDateInLocal(now),
    weekdayKey: dateToWeekdayKey(now),
  };
}

/**
 * 수업시간 LS뿐 아니라 연간진도표·프로그램진도 캐시·RQ meta에 들어 있는 schedule도 사용.
 */
export function resolveClassScheduleSettingsFromLocalCaches(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeIds?: string[];
}): ClassScheduleSettings | null {
  const sid = params.workspaceId.trim();
  if (!sid || typeof window === 'undefined') return null;

  const years = Array.from(
    new Set(
      [params.academicYear, params.academicYear - 1, params.academicYear + 1].filter((y) =>
        Number.isFinite(y)
      )
    )
  );
  const terms: Array<1 | 2> = [params.termNumber, params.termNumber === 1 ? 2 : 1];
  const gradeIds = (params.gradeIds ?? []).map((id) => id.trim()).filter(Boolean);

  const candidates: ClassScheduleSettings[] = [];
  const push = (schedule: ClassScheduleSettings | null | undefined) => {
    if (schedule) candidates.push(schedule);
  };

  for (const gradeId of gradeIds) {
    push(
      readAnnualProgressMetaCacheOrQuery(sid, params.academicYear, params.termNumber, gradeId).data
        .schedule
    );
  }

  push(loadAnnualProgressDataCache(sid, params.academicYear, params.termNumber).state.schedule);

  for (const gradeId of gradeIds) {
    push(
      loadProgramProgressDataCache(sid, params.academicYear, params.termNumber, gradeId).state.schedule
    );
  }

  push(loadClassScheduleSettingsCache(sid));

  for (const y of years) {
    for (const t of terms) {
      if (y === params.academicYear && t === params.termNumber) continue;
      for (const gradeId of gradeIds) {
        push(readAnnualProgressMetaCacheOrQuery(sid, y, t, gradeId).data.schedule);
      }
      push(loadAnnualProgressDataCache(sid, y, t).state.schedule);
      for (const gradeId of gradeIds) {
        push(loadProgramProgressDataCache(sid, y, t, gradeId).state.schedule);
      }
    }
  }

  const withTimes = candidates.find(scheduleHasClassStartTimes);
  if (withTimes) return withTimes;
  return candidates[0] ?? null;
}

/**
 * 대시보드·연간진도표와 동일 RQ 키(`annualProgressMeta`)로 수업시간을 확보.
 * 캐시 miss 시에만 네트워크.
 */
export async function ensureClassScheduleSettingsForAlarm(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeIds?: string[];
}): Promise<ClassScheduleSettings | null> {
  const sid = params.workspaceId.trim();
  if (!sid) return null;

  const gradeIds = (params.gradeIds ?? []).map((id) => id.trim()).filter(Boolean);
  const anchorGradeId = gradeIds[0] ?? '';

  const local = resolveClassScheduleSettingsFromLocalCaches(params);
  if (local && scheduleHasClassStartTimes(local)) return local;

  if (anchorGradeId) {
    try {
      const meta = await ensureAnnualProgressMetaQueryData({
        workspaceId: sid,
        academicYear: params.academicYear,
        termNumber: params.termNumber,
        gradeId: anchorGradeId,
      });
      if (meta.schedule && scheduleHasClassStartTimes(meta.schedule)) return meta.schedule;
      if (meta.schedule) return meta.schedule;
    } catch {
      /* fall through */
    }
  }

  try {
    const fromDb = await getClassScheduleSettings(sid);
    if (fromDb && scheduleHasClassStartTimes(fromDb)) return fromDb;
    return fromDb ?? local;
  } catch {
    return local;
  }
}

/**
 * 대시보드·연간진도표와 동일 RQ 키(`annualTimetableCells`)로 셀을 확보한다.
 * - 학년별로 schoolKeys 캐시 hit면 재사용
 * - 해당 날짜 편성이 캐시에 없으면 같은 키로 forceNetwork fetch
 */
export async function ensureAnnualTimetableCellsForAlarm(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeIds: string[];
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
}): Promise<Record<string, AnnualTimetableCell>> {
  const sid = params.workspaceId.trim();
  const gradeIds = Array.from(new Set(params.gradeIds.map((id) => id.trim()).filter(Boolean)));
  if (!sid || !gradeIds.length) return {};

  const { mondayIso, weekdayKey, academicYear, termNumber } = params;
  const allRows: AnnualTimetableCell[] = [];

  for (const gradeId of gradeIds) {
    try {
      let rows = await ensureAnnualTimetableCellsQueryData({
        workspaceId: sid,
        academicYear,
        termNumber,
        gradeId,
        forceNetwork: false,
      });
      // 학기 캐시에 해당 요일 행이 전혀 없으면(불완전/미로드) 같은 RQ 키로 갱신
      if (!rowsCoverDay(rows, mondayIso, weekdayKey)) {
        rows = await ensureAnnualTimetableCellsQueryData({
          workspaceId: sid,
          academicYear,
          termNumber,
          gradeId,
          forceNetwork: true,
        });
      }
      allRows.push(...rows);
    } catch {
      const fallback = readAnnualTimetableCellsCacheOrQuery(sid, academicYear, termNumber, gradeId);
      if (fallback.found) allRows.push(...fallback.rows);
    }
  }

  const merged = resolveMergedTimetableCellsByKey({
    workspaceId: sid,
    academicYear,
    termNumber,
    gradeIds,
    networkCellsByKey: buildAnnualCellMap(allRows),
  });
  overlayCellsIntoMap(merged, allRows);
  return merged;
}

export type ClassLessonAlarmEnsuredData = {
  academicYear: number;
  termNumber: 1 | 2;
  cellsByKey: Record<string, AnnualTimetableCell>;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey | null;
};

/** 수업시간 + 오늘 연간시간표 셀을 한 번에 준비 (대시보드·연간진도표와 동일 RQ 키) */
export async function ensureClassLessonAlarmData(params: {
  workspaceId: string;
  grades: Grade[];
  now?: Date;
  programSidebarAcademicYear?: number | null;
}): Promise<ClassLessonAlarmEnsuredData> {
  const now = params.now ?? new Date();
  const sid = params.workspaceId.trim();
  const { academicYear, termNumber } = resolveAlarmAcademicYearTerm(
    sid,
    now,
    params.programSidebarAcademicYear ?? null
  );
  const gradeIds = params.grades.map((g) => g.id);
  const { mondayIso, weekdayKey } = resolveAlarmDayKeys(now);

  await ensureClassScheduleSettingsForAlarm({
    workspaceId: sid,
    academicYear,
    termNumber,
    gradeIds,
  });

  let cellsByKey: Record<string, AnnualTimetableCell> = {};
  const alarmWeekday = isClassLessonAlarmWeekdayKey(weekdayKey) ? weekdayKey : null;
  if (alarmWeekday) {
    cellsByKey = await ensureAnnualTimetableCellsForAlarm({
      workspaceId: sid,
      academicYear,
      termNumber,
      gradeIds,
      mondayIso,
      weekdayKey: alarmWeekday,
    });
  }

  return { academicYear, termNumber, cellsByKey, mondayIso, weekdayKey: alarmWeekday };
}

function loadProgramColorSettingsSync(workspaceId: string): ProgramColorSettings {
  try {
    const raw = deviceCacheStorage.getItem(`cp.programColorSettings.v2.${workspaceId}`);
    if (!raw) {
      return { colorsByGrade: {}, baseTimetableByGrade: {}, workspaceFallback: null };
    }
    const parsed = JSON.parse(raw) as Partial<ProgramColorSettings>;
    return {
      colorsByGrade: parsed.colorsByGrade && typeof parsed.colorsByGrade === 'object' ? parsed.colorsByGrade : {},
      baseTimetableByGrade:
        parsed.baseTimetableByGrade && typeof parsed.baseTimetableByGrade === 'object'
          ? parsed.baseTimetableByGrade
          : {},
      workspaceFallback: parsed.workspaceFallback ?? null,
    };
  } catch {
    return { colorsByGrade: {}, baseTimetableByGrade: {}, workspaceFallback: null };
  }
}

function resolveCellLesson(
  cell: AnnualTimetableCell | undefined,
  dedicatedColors: string[]
): { hasLesson: boolean; channel: 'regular' | 'dedicated' } {
  const text = (cell?.text_value ?? '').trim();
  const colorHex = (cell?.color_hex ?? '').trim();
  const hasLesson = annualSlotHasTimetableProgramAssignment(text, colorHex);
  if (!hasLesson) return { hasLesson: false, channel: 'regular' };
  const isDedicated =
    cell?.is_dedicated === true || isDedicatedColor(colorHex, dedicatedColors);
  return { hasLesson: true, channel: isDedicated ? 'dedicated' : 'regular' };
}

/**
 * 오늘(월~토) 알람 블록:
 * - 기준: 수업시간 설정의 `수업(class)` 행(+시작시각) 전부
 * - 활성(hasLesson): 연간시간표 오늘 칸에 실제 수업(텍스트·색) 편성 — 대시보드와 동일 셀 출처
 * - 울림(willRing): 활성 + 해당 채널(일반/전담) 사용
 */
export function buildTodayClassLessonAlarmTargets(params: {
  workspaceId: string;
  grades: Grade[];
  academicYear: number;
  termNumber: 1 | 2;
  settings: ClassLessonAlarmSettings;
  now?: Date;
  /** false면 동일 시각·채널도 교시별로 모두 표시 (설정 미리보기용). 기본 true */
  dedupe?: boolean;
  /** true면 willRing 대상만 반환 (실제 울림용). 기본 false */
  ringOnly?: boolean;
  /** 미리 확보한 연간 셀(없으면 LS merge) */
  cellsByKey?: Record<string, AnnualTimetableCell>;
}): ClassLessonAlarmTarget[] {
  if (!params.settings.masterEnabled) return [];

  const now = params.now ?? new Date();
  const { mondayIso, weekdayKey: dayKey } = resolveAlarmDayKeys(now);
  // 연간시간표와 동일: 월~토 (일요일만 제외)
  const weekdayKey = isClassLessonAlarmWeekdayKey(dayKey) ? dayKey : null;
  if (!weekdayKey) return [];

  const sid = params.workspaceId.trim();
  if (!sid) return [];

  const schedule = resolveClassScheduleSettingsFromLocalCaches({
    workspaceId: sid,
    academicYear: params.academicYear,
    termNumber: params.termNumber,
    gradeIds: params.grades.map((g) => g.id),
  });
  if (!schedule) return [];

  const entries = buildAnnualClassEntries(schedule, params.grades);
  if (!entries.length) return [];

  const colorSettings = loadProgramColorSettingsSync(sid);
  const gradeIds = entries.map((e) => e.gradeId);
  const cellsByKey =
    params.cellsByKey ??
    resolveMergedTimetableCellsByKey({
      workspaceId: sid,
      academicYear: params.academicYear,
      termNumber: params.termNumber,
      gradeIds,
    });

  const out: ClassLessonAlarmTarget[] = [];
  const seen = new Set<string>();
  const shouldDedupe = params.dedupe !== false;

  for (const entry of entries) {
    const { dedicated } = resolveGradeProgramColors(colorSettings, entry.gradeId);
    let classPeriodOrdinal = 0;
    const weekdayForTimes = isWeekdayTimeKey(weekdayKey) ? weekdayKey : null;
    const resolvedRows = resolveClassScheduleRowsForWeekday(schedule, entry.sectionId, weekdayForTimes);

    for (let slotIndex = 0; slotIndex < resolvedRows.length; slotIndex += 1) {
      const row = resolvedRows[slotIndex];
      if (!row || row.kind !== 'class') continue;
      classPeriodOrdinal += 1;

      const time = normalizeHHMM(row.startTime);
      if (!time) continue;

      const cellKey = buildAnnualCellKey(entry.gradeId, mondayIso, weekdayKey, slotIndex);
      const cell = cellsByKey[cellKey];
      const { hasLesson, channel } = resolveCellLesson(cell, dedicated ?? []);
      const channelSettings = params.settings[channel];
      const willRing = hasLesson && channelSettings.enabled;

      if (params.ringOnly && !willRing) continue;

      if (shouldDedupe) {
        const dedupeKey = `${time}|${channel}|${channelSettings.offset}|${channelSettings.soundKind}|${hasLesson ? '1' : '0'}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
      }

      out.push({
        time,
        offset: channelSettings.offset,
        soundKind: channelSettings.soundKind,
        channel,
        gradeId: entry.gradeId,
        gradeLabel: entry.label,
        slotIndex,
        periodLabel: `${classPeriodOrdinal}교시`,
        hasLesson,
        willRing,
      });
    }
  }

  return out.sort((a, b) => {
    const byTime = a.time.localeCompare(b.time);
    if (byTime !== 0) return byTime;
    const byGrade = a.gradeLabel.localeCompare(b.gradeLabel, 'ko');
    if (byGrade !== 0) return byGrade;
    return a.slotIndex - b.slotIndex;
  });
}
